// ============================================================
// TILT — tiltRelay.js            QUICK CO-OP: THE RELAY  (caP'n, 2026-10-25)
//
//   "each player gets their own bank of 2 Anima (balls) and can get more or lose theirs etc.
//    One person at a time shoot a ball, do the best they can, next person comes in tries to
//    finish the job, rinse repeat :3"
//
// ONE shared run of the Cirque. On your turn you shoot ONE ball; when it drains, everything you did
// carries to the next player - the team score, Willie's wounds, the tickets, the heat, the train.
//   · BANK: everyone starts with START_BANK Anima. A drain spends one. You EARN one into your OWN bank
//     for felling Willie or riding the Ghost Train (EARN_ON - a dial). An empty bank sits out.
//   · TURNS: round-robin in join order over the players who still have Anima. The run ends when
//     nobody does.
//   · JOINING: up to MAX_RELAY players, only BEFORE THE FIRST BALL (Mini Wili's 2026-10-25 ruling).
//
// Borrowed whole in shape from Mini Wili (miniwiliRound.js - the borrow-first rule): pure rules + an
// `io` adapter, rows under the open games umbrella (no rules deploy, no new top-level node), the host
// keeps house, a stuck turn is passed by CAS so only one client wins.
//
// THIS FILE IS PURE: no React, no firebase import - a pretend database runs it headless with bots
// (that's how it was proven; see TILT_PRIME §P2.W). tiltRelayShell.js is the thin React/firebase shell.
//
// ── THE NODES ─────────────────────────────────────────────────
//   games/tilt/servers/{sid}/relay/run      { id, rk, by, over, balls, sc, kills, snap }
//        run id (+1 per run) · run key · who opened it · over(0|1) · balls played · TEAM score ·
//        Willies felled · snap = the table, as a JSON string, after the last ball (null = a fresh table)
//   games/tilt/servers/{sid}/relay/turn     { seq, r, uid, at, go }
//        whose ball it is · seq +1 every pass · r = the run id · at = server ms the turn began ·
//        go = the uid whose ball is LIVE, CLAIMED BY CAS before the ball screen opens (review fix: a separate
//        "I'm shooting" write could lose the race with a 60 s skip and put TWO balls in play)
//   games/tilt/servers/{sid}/relay/p/{uid}  { n, j, in, rk, bank, sc, fl, gt, sh, ms, ts }
//        name · join order · in the run? · the run key my bank belongs to · Anima left · MY points ·
//        my fells · my Ghost Trains · sh = the turn seq I'm SHOOTING (my ball is live - nobody times me
//        out) · turns missed · last write
// ⚑ WRITES ARE PER BALL, NEVER PER FRAME: one "I'm shooting" + one ball-over (run + row + turn pass)
//   per ball, two writes per run for the host. Nothing writes while the ball is rolling.
// ============================================================

export const RELAY_BASE = (sid) => `games/tilt/servers/${sid}/relay`;
export const START_BANK = 2;          // ⟵ DIAL  Anima each player starts with (caP'n: 2)
export const MAX_RELAY = 4;           // ⟵ DIAL  players in one run
export const EARN_ON = { fell: 1, ghost: 1 };   // ⟵ DIAL  Anima earned into YOUR bank: per Willie felled, per Ghost Train
export const TURN_MS = 60000;         // ⟵ DIAL  you have this long to START your ball (a ball in play is never timed)
export const AWAY_AFTER = 2;          // ⟵ DIAL  missed turns in a row before you're skipped without the wait
export const DROP_AFTER = 3;          // ⟵ DIAL  missed turns in a row before your bank is emptied (nobody waits forever)
export const SKIP_STAGGER_MS = 700;   // ⟵ DIAL
export const GONE_SKIP_MS = 300;      // ⟵ DIAL  the holder left / has no Anima: pass it almost at once
export const AWAY_SKIP_MS = 1500;     // ⟵ DIAL
export const TINTS = ["#f0b429", "#7ce7ff", "#ff6f8e", "#9ef01a"];   // ⟵ DIAL  by join order

const num = (v) => (typeof v === "number" && isFinite(v) ? v : 0);
const clipN = (s) => String(s || "Player").slice(0, 24);
const P = (base, uid) => `${base}/p/${uid}`;
export const rowPatch = (base, uid, fields) =>
  Object.fromEntries(Object.entries(fields).map(([k, v]) => [`${P(base, uid)}/${k}`, v]));

// ── THE TABLE BETWEEN BALLS ──────────────────────────────────────
// What carries from one player's ball to the next: the durable state of the run, never the ball itself
// or a moment's machinery (a hot piston, a timer). Sets travel as arrays.
const TABLE_KEYS = ["crucibleLit", "jackpots", "gateOpen", "roar", "vaults", "loops", "spins",
  "heat", "phase", "meltTimer", "kills", "strikes", "trainA"];
export function captureTable(engine) {
  const s = engine.table._s ? engine.table._s() : {};
  const t = {};
  for (const k of TABLE_KEYS) if (s[k] !== undefined) t[k] = s[k];
  t.lit = [...(s.lit || [])];
  t.tickets = [...(s.tickets || [])];
  const B = engine.boss;
  return {
    v: 1, sc: engine.score, ch: engine.charge, mu: engine.multiplier, t,
    b: B ? { a: B.anima, l: B.lyf, f: !!B.fractured } : null,
  };
}
export function restoreTable(engine, snap) {
  if (!snap || snap.v !== 1) return false;
  const s = engine.table._s ? engine.table._s() : null;
  if (s) {
    for (const k of TABLE_KEYS) if (snap.t && snap.t[k] !== undefined) s[k] = snap.t[k];
    s.lit = new Set((snap.t && snap.t.lit) || []);
    s.tickets = new Set((snap.t && snap.t.tickets) || []);
    if (s.roar && engine.table.relayRoar) engine.table.relayRoar(engine);   // the table re-arms what ROAR changed
  }
  engine.score = num(snap.sc);
  engine.charge = num(snap.ch);
  // (the multiplier is NOT carried: every ball starts at x1, exactly as a new ball does in solo play)
  if (engine.boss && snap.b) {
    engine.boss.anima = num(snap.b.a);
    engine.boss.lyf = num(snap.b.l);
    engine.boss.fractured = !!snap.b.f;
  }
  return true;
}
export const packSnap = (snap) => JSON.stringify(snap);
export const unpackSnap = (s) => { try { return s ? JSON.parse(s) : null; } catch (e) { return null; } };
// Anima earned on ONE ball: how many fells / Ghost Trains happened between the snapshot I started from
// and the one I ended on.
export function earnedOn(before, after) {
  const k0 = num(before && before.t && before.t.kills), k1 = num(after && after.t && after.t.kills);
  const v0 = num(before && before.t && before.t.vaults), v1 = num(after && after.t && after.t.vaults);
  const fells = Math.max(0, k1 - k0), ghosts = Math.max(0, v1 - v0);
  return { fells, ghosts, anima: fells * EARN_ON.fell + ghosts * EARN_ON.ghost };
}

// ── READING THE ROWS ─────────────────────────────────────────────
export function presentOrder(rawRows, seatUids) {
  const out = [];
  for (const [uid, r] of Object.entries(rawRows || {})) {
    if (!r || typeof r !== "object") continue;
    if (seatUids && !seatUids.includes(uid)) continue;
    out.push({ ...r, uid });
  }
  out.sort((a, b) => (num(a.j) - num(b.j)) || (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0));
  return out;
}
export const inRun = (p, run) => !!p && !!p.in && !!run && p.rk === run.rk;
export const bankOf = (p, run) => (inRun(p, run) ? num(p.bank) : 0);
export const hostOf = (order, run) => { const p = order.find((q) => inRun(q, run)); return p ? p.uid : null; };
export const isLive = (run, order) => !!run && !run.over && order.some((p) => inRun(p, run));
export function joinable(run, order, turn) {
  if (!run || run.over || num(run.balls) > 0) return false;
  if (turn && turn.r === run.id && turn.go) return false;            // the first ball is already rolling
  const players = order.filter((p) => inRun(p, run));
  return players.length > 0 && players.length < MAX_RELAY;
}

// ── WHOSE BALL IS NEXT ───────────────────────────────────────────
export function nextTurn(order, run, afterUid) {
  const cands = order.filter((p) => inRun(p, run) && bankOf(p, run) > 0);
  if (!cands.length) return null;
  const awake = cands.filter((p) => num(p.ms) < AWAY_AFTER);
  const pool = awake.length ? awake : cands;
  const idxAfter = afterUid ? order.findIndex((p) => p.uid === afterUid) : -1;
  for (const p of pool) if (order.indexOf(p) > idxAfter) return p.uid;
  return pool[0].uid;
}
export const runSpent = (order, run) => !order.some((p) => inRun(p, run) && bankOf(p, run) > 0);

// THE HOST (first in the run by join order) ends a spent run and hands out a turn when none stands.
export function hostIntent({ order, run, turn, uid }) {
  if (!run || run.over || hostOf(order, run) !== uid) return null;
  const holder = turn && turn.r === run.id && turn.uid ? order.find((p) => p.uid === turn.uid) : null;
  const shooting = !!holder && !!turn.go && inRun(holder, run);
  if (runSpent(order, run) && !shooting) return { type: "end", id: run.id };
  if (!(turn && turn.r === run.id && turn.uid)) return { type: "assign", id: run.id, seq: turn ? num(turn.seq) : 0 };
  return null;
}
// ANYONE ELSE passes a stuck turn: the holder left, ran dry, went away, or never started their ball in TURN_MS.
// A ball that is IN PLAY (row.sh === turn.seq) is never timed out - only its owner leaving passes it.
export function skipIntent({ order, run, turn, uid, now }) {
  if (!run || run.over || !turn || turn.r !== run.id || !turn.uid || turn.uid === uid) return null;
  const others = order.filter((p) => inRun(p, run) && p.uid !== turn.uid);
  const rank = others.findIndex((p) => p.uid === uid);
  if (rank < 0) return null;
  const holder = order.find((p) => p.uid === turn.uid);
  const valid = !!holder && inRun(holder, run) && bankOf(holder, run) > 0;
  if (valid && turn.go) return null;                                  // a claimed ball in play: never rushed (only leaving passes it)
  let delay;
  if (!valid) delay = GONE_SKIP_MS;
  else if (num(holder.ms) >= AWAY_AFTER) delay = AWAY_SKIP_MS;
  else delay = Math.max(0, (num(turn.at) || now) + TURN_MS - now);
  return { who: turn.uid, seq: num(turn.seq), valid, force: !valid, delay: delay + rank * SKIP_STAGGER_MS };
}

// ── DERIVED VIEWS ────────────────────────────────────────────────
const tintAt = (order, uid) => TINTS[Math.max(0, order.findIndex((p) => p.uid === uid)) % TINTS.length];
export function stripOf(order, run, turn, myUid) {
  if (!run) return [];
  return order.filter((p) => inRun(p, run)).map((p) => ({
    uid: p.uid, n: p.n || "Player", tint: tintAt(order, p.uid), me: p.uid === myUid,
    bank: bankOf(p, run), sc: num(p.sc), fl: num(p.fl), gt: num(p.gt),
    away: num(p.ms) >= AWAY_AFTER,
    turn: !!turn && turn.r === run.id && turn.uid === p.uid,
    shooting: !!turn && turn.r === run.id && turn.uid === p.uid && turn.go === p.uid,
  }));
}

// ── THE WRITES (io: update(paths) · cas(path, fn) · onDisc(path) · cancelDisc · remove · nowInt) ──
const blankRow = (me, io) => ({ n: clipN(me.n), j: me.j, in: 0, rk: 0, bank: 0, sc: 0, fl: 0, gt: 0, sh: -1, ms: 0, ts: io.nowInt() });
export async function enterServer(io, base, me) {
  await io.onDisc(P(base, me.uid));                                  // onDisconnect FIRST (ieSocial's ordering law)
  await io.update({ [P(base, me.uid)]: blankRow(me, io) });
}
export const renameMe = (io, base, uid, n) => io.update({ [`${P(base, uid)}/n`]: clipN(n) });
const freshRow = (rk) => ({ in: 1, rk, bank: START_BANK, sc: 0, fl: 0, gt: 0, sh: -1, ms: 0 });
// I open a run: a fresh Cirque, and I'm in it with a full bank.
export async function startRun(io, base, me, prevRun) {
  const id = num(prevRun && prevRun.id) + 1;
  const rk = io.nowInt();
  // CAS (review): two people pressing START at once can't both open a run - the loser just sees JOIN
  const ok = await io.cas(`${base}/run`, (cur) =>
    (!cur || cur.over || num(cur.id) < id) ? { id, rk, by: me.uid, over: 0, balls: 0, sc: 0, kills: 0, snap: "" } : undefined);
  if (!ok) return 0;
  await io.update(rowPatch(base, me.uid, freshRow(rk)));
  return id;
}
export async function joinRun(io, base, me, run, order) {
  if (!joinable(run, order)) return false;
  await io.update(rowPatch(base, me.uid, freshRow(run.rk)));
  return true;
}
const passTurn = (io, base, run, who, seq, nxt, mine = false, force = false) => io.cas(`${base}/turn`, (cur) =>
  (cur && cur.uid === who && num(cur.seq) === seq && cur.r === run.id && (!cur.go || mine || force))
    ? { seq: num(cur.seq) + 1, r: run.id, uid: nxt || "", at: io.nowInt(), go: "" } : undefined);
// I pressed SHOOT on my turn: CLAIM the ball on the turn node (CAS). Only if it commits does the ball screen open;
// from then on nobody can pass my turn while I'm here. Resolves true/false.
export async function startBall(io, base, uid, turn) {
  const seq = num(turn && turn.seq), r = turn && turn.r;
  const ok = await io.cas(`${base}/turn`, (cur) =>
    (cur && cur.uid === uid && num(cur.seq) === seq && cur.r === r && !cur.go) ? { ...cur, go: uid } : undefined);
  if (ok) await io.update(rowPatch(base, uid, { sh: seq, ms: 0 }));
  return ok;
}
// My ball drained. Hand the table on: the run takes my snapshot (CAS on the ball count, so a stale client can never
// overwrite a newer table), my bank pays 1 and earns what I won, my turn passes.
export async function ballOver(io, base, me, run, turn, order, { snap, before, balls0: ballsAtShoot, seq: mySeq }) {
  const mine = order.find((p) => p.uid === me.uid) || {};
  const earned = earnedOn(before, snap);
  const delta = Math.max(0, num(snap && snap.sc) - num(before && before.sc));
  const bank = Math.max(0, bankOf(mine, run) - 1 + earned.anima);
  const fields = { bank, sc: num(mine.sc) + delta, fl: num(mine.fl) + earned.fells, gt: num(mine.gt) + earned.ghosts, sh: -1, ms: 0, ts: io.nowInt() };
  // the ball count AS IT WAS WHEN I PRESSED SHOOT (review: the latest cache made this CAS a no-op, so a stale ball
  // could overwrite a newer table)
  const balls0 = ballsAtShoot != null ? num(ballsAtShoot) : num(run.balls);
  const seq = mySeq != null ? num(mySeq) : num(turn && turn.seq);
  let okRun = false;
  try {
    okRun = await io.cas(`${base}/run`, (cur) =>
      (cur && cur.id === run.id && !cur.over && num(cur.balls) === balls0)
        ? { ...cur, balls: balls0 + 1, sc: num(snap && snap.sc), kills: num(snap && snap.t && snap.t.kills), snap: packSnap(snap) }
        : undefined);
  } finally {
    // ALWAYS release: my row and my turn pass go out even if the run write failed (review: a rejected run write
    // used to leave the ball "live" forever and lock the whole server)
    const patched = order.map((p) => (p.uid === me.uid ? { ...p, ...fields } : p));
    const nxt = nextTurn(patched, run, me.uid);
    await io.update(rowPatch(base, me.uid, fields)).catch(() => {});
    await passTurn(io, base, run, me.uid, seq, nxt, true).catch(() => false);
  }
  return { okRun, bank, earned, delta };
}
export async function skipTurn(io, base, intent, order, run) {
  const nxt = nextTurn(order, run, intent.who);
  const ok = await passTurn(io, base, run, intent.who, intent.seq, nxt, false, !!intent.force);
  if (ok && intent.valid) {
    const h = order.find((p) => p.uid === intent.who);
    const ms = num(h && h.ms) + 1;
    await io.update(ms >= DROP_AFTER ? rowPatch(base, intent.who, { ms, bank: 0 }) : { [`${P(base, intent.who)}/ms`]: ms });
  }
  return ok;
}
export const endRun = (io, base, run) => io.cas(`${base}/run`, (cur) =>
  (cur && cur.id === run.id && !cur.over) ? { ...cur, over: 1 } : undefined);
export const assignTurn = (io, base, run, order, turn) => io.cas(`${base}/turn`, (cur) =>
  (!cur || cur.r !== run.id || !cur.uid) && num(cur && cur.seq) === num(turn && turn.seq)
    ? { seq: num(cur && cur.seq) + 1, r: run.id, uid: nextTurn(order, run, turn && turn.uid) || "", at: io.nowInt() } : undefined);
export async function leaveRun(io, base, me, run, turn, order) {
  if (run && turn && turn.uid === me.uid && turn.r === run.id) {
    const patched = order.map((p) => (p.uid === me.uid ? { ...p, in: 0 } : p));
    await passTurn(io, base, run, me.uid, num(turn.seq), nextTurn(patched, run, me.uid), true);
  }
  await io.update(rowPatch(base, me.uid, { in: 0, sh: -1, ms: 0 }));
}
export const imBack = (io, base, uid) => io.update(rowPatch(base, uid, { ms: 0 }));
